import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { CASE_DOCUMENT_STATUS, CASE_FILE_KIND, CaseDocumentFileModel } from "@shared/models";
import { selectors } from "@containers/Cases/store";
import { CASE_DOCUMENT_MODAL_STAGE } from "@containers/Cases/constants";
import { useCaseDocumentParsingFailed } from "@containers/Cases/hooks";
import { getSubjectName } from "@containers/Cases/utils/caseDocuments";

import CaseNotesUploadStep from "./CaseNotesUploadStep";
import CaseNotesParsingStep from "./CaseNotesParsingStep";
import CaseNotesResultStep from "./CaseNotesResultStep";

const NO_FILES: CaseDocumentFileModel[] = [];

interface UploadCaseNotesModalProps {
  caseId: number;
  initialStage?: CASE_DOCUMENT_MODAL_STAGE;
  /** Opens the upload step on the current file, to replace it */
  replace?: boolean;
  onClose: () => void;
}

const UploadCaseNotesModal: FC<UploadCaseNotesModalProps> = ({
  caseId,
  initialStage = CASE_DOCUMENT_MODAL_STAGE.UPLOAD,
  replace = false,
  onClose,
}) => {
  const [stage, setStage] = useState(initialStage);
  const [isReplacing, setIsReplacing] = useState(replace);
  const caseObj = useSelector(selectors.getCase());
  const caseDocuments = useSelector(selectors.getCaseDocuments());

  const caseNotes = caseDocuments.find(({ kind }) => kind === CASE_FILE_KIND.NOTES);
  const isFailed = useCaseDocumentParsingFailed(caseNotes);
  const hasNoNotes = stage === CASE_DOCUMENT_MODAL_STAGE.STATUS && !caseNotes;

  useEffect(() => {
    if (hasNoNotes) onClose();
  }, [hasNoNotes, onClose]);

  const subtitle = `Prisonology notes from conversations with or about ${getSubjectName(caseObj) || "the case subject"}. The text is saved as written, with headers and footers removed. It isn’t summarised or used in scoring.`;

  if (stage === CASE_DOCUMENT_MODAL_STAGE.UPLOAD) {
    return (
      <CaseNotesUploadStep
        caseId={caseId}
        documentFiles={caseNotes?.files ?? NO_FILES}
        replace={isReplacing}
        subtitle={subtitle}
        onClose={onClose}
        onParse={() => setStage(CASE_DOCUMENT_MODAL_STAGE.STATUS)}
      />
    );
  }

  if (!caseNotes) return null;

  if (caseNotes.status === CASE_DOCUMENT_STATUS.PARSING) {
    return (
      <CaseNotesParsingStep
        title={isReplacing ? "Replace Case Notes" : "Upload Case Notes"}
        subtitle={subtitle}
        caseDocument={caseNotes}
      />
    );
  }

  return (
    <CaseNotesResultStep
      caseId={caseId}
      caseDocument={caseNotes}
      isFailed={isFailed}
      caseNotesText={caseObj?.case_notes ?? null}
      onClose={onClose}
      onReplaceFile={() => {
        setIsReplacing(true);
        setStage(CASE_DOCUMENT_MODAL_STAGE.UPLOAD);
      }}
    />
  );
};

export default UploadCaseNotesModal;
